import { Fragment, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ChevronDown,
  ChevronRight,
  Eye,
  EyeOff,
  FolderTree,
  Pencil,
  Plus,
  RefreshCw,
  Save,
  Trash2,
  X,
} from 'lucide-react';
import apiClient from '../../api/client';
import { useToast } from '../../components/ui/Toast';
import { PageHeader } from '../../components/ui/PageHeader';
import { Button } from '../../components/ui/Button';
import { usePermission } from '../../auth/usePermission';
import { PermissionName } from '../../auth/permissions';
import { useConfirm } from '../../components/ui/ConfirmDialog';
import { inputClass } from '../../components/ui/Input';

interface CatNode {
  id: string;
  name: string;
  code: string;
  source: 'TECDOC' | 'CUSTOM';
  kind: 'NODE' | 'GENERIC' | null;
  level: number | null;
  active: boolean;
  hasChildren: boolean;
  productCount: number;
  /** TecDoc's own id — STR_ID for a node, PT_ID for a generic article. Null on a custom one. */
  tecdocId: number | null;
  /** Brands to switch under this category: TecDoc's list for its generic article ∪ our products'. */
  hasBrands: boolean;
}

/** A brand under a leaf category (migration 106). Not a filing target — a switch on an intersection. */
interface CatBrand {
  brandKey: string;
  brandName: string;
  source: 'TECDOC' | 'CATALOG';
  /** Articles TecDoc holds for this brand under this generic article. */
  articleCount: number;
  /** Our products filed here with this brand — exactly what switching it off hides. */
  productCount: number;
  active: boolean;
}

interface SyncLogRow {
  id: string;
  status: 'RUNNING' | 'COMPLETED' | 'FAILED';
  category_brands_synced: number | null;
  error_log: string | null;
}

/** Rows shown before "show all": brake pads alone carry 294 brands. */
const BRAND_PAGE = 40;

interface ModalState {
  mode: 'create' | 'edit';
  parentId: string | null; // for create
  parentName?: string;
  node?: CatNode; // for edit
  name: string;
}

const cmd = () => ({ headers: { 'X-Command-Id': crypto.randomUUID() } });

/** Admin — product category tree (TecDoc search tree mirror + custom categories). */
export default function CategoryTree() {
  const { t } = useTranslation();
  const [confirm, confirmDialog] = useConfirm();
  const { showApiError, push } = useToast();
  const canWrite = usePermission(PermissionName.CATALOG_WRITE);
  const [roots, setRoots] = useState<CatNode[] | null>(null);
  const [childrenMap, setChildrenMap] = useState<Record<string, CatNode[]>>({});
  const [parentByChild, setParentByChild] = useState<Record<string, string | null>>({});
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [loadingNode, setLoadingNode] = useState<Set<string>>(new Set());
  const [syncing, setSyncing] = useState(false);
  const [modal, setModal] = useState<ModalState | null>(null);
  const [busy, setBusy] = useState(false);
  const [brandsMap, setBrandsMap] = useState<Record<string, CatBrand[]>>({});
  const [brandFilter, setBrandFilter] = useState<Record<string, string>>({});
  const [brandShowAll, setBrandShowAll] = useState<Set<string>>(new Set());
  const [syncingBrands, setSyncingBrands] = useState(false);

  const load = async (parentId: string | null) => {
    const { data } = await apiClient.get<CatNode[]>(
      `/products/categories/tree${parentId ? `?parentId=${parentId}` : ''}`
    );
    if (parentId === null) setRoots(data);
    else setChildrenMap((m) => ({ ...m, [parentId]: data }));
    setParentByChild((m) => {
      const next = { ...m };
      for (const c of data) next[c.id] = parentId;
      return next;
    });
  };

  const loadBrands = async (categoryId: string) => {
    const { data } = await apiClient.get<CatBrand[]>(`/products/categories/${categoryId}/brands`);
    setBrandsMap((m) => ({ ...m, [categoryId]: data }));
  };

  /** Every category row loaded so far, whichever level it came from. */
  const loadedNodes = (): CatNode[] => [...(roots ?? []), ...Object.values(childrenMap).flat()];

  useEffect(() => {
    load(null).catch(showApiError);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const toggle = async (n: CatNode) => {
    if (expanded.has(n.id)) {
      setExpanded((s) => {
        const x = new Set(s);
        x.delete(n.id);
        return x;
      });
      return;
    }
    const needChildren = n.hasChildren && !childrenMap[n.id];
    const needBrands = n.hasBrands && !brandsMap[n.id];
    if (needChildren || needBrands) {
      setLoadingNode((s) => new Set(s).add(n.id));
      try {
        await Promise.all([
          needChildren ? load(n.id) : Promise.resolve(),
          needBrands ? loadBrands(n.id) : Promise.resolve(),
        ]);
      } catch (err) {
        showApiError(err);
        setLoadingNode((s) => {
          const x = new Set(s);
          x.delete(n.id);
          return x;
        });
        return;
      }
      setLoadingNode((s) => {
        const x = new Set(s);
        x.delete(n.id);
        return x;
      });
    }
    setExpanded((s) => new Set(s).add(n.id));
  };

  const refresh = (parentId: string | null) => load(parentId).catch(showApiError);

  const sync = async () => {
    setSyncing(true);
    try {
      const { data } = await apiClient.post<{ data: { nodes: number; generics: number } }>(
        '/admin/tecdoc/sync-tree',
        {},
        cmd()
      );
      push({
        tone: 'success',
        title: t('categoryAdmin.toast.treeSyncedTitle'),
        message: t('categoryAdmin.toast.treeSyncedMessage', {
          nodes: data.data.nodes,
          generics: data.data.generics,
        }),
      });
      setChildrenMap({});
      setExpanded(new Set());
      await refresh(null);
    } catch (err) {
      showApiError(err);
    } finally {
      setSyncing(false);
    }
  };

  const submitModal = async () => {
    if (!modal || !modal.name.trim()) return;
    setBusy(true);
    try {
      if (modal.mode === 'create') {
        await apiClient.post(
          '/products/categories',
          { name: modal.name.trim(), parentId: modal.parentId },
          cmd()
        );
        push({ tone: 'success', message: t('categoryAdmin.toast.created') });
        if (modal.parentId) {
          await refresh(modal.parentId);
          setExpanded((s) => new Set(s).add(modal.parentId!));
        } else {
          await refresh(null);
        }
      } else if (modal.node) {
        await apiClient.patch(
          `/products/categories/${modal.node.id}`,
          { name: modal.name.trim() },
          cmd()
        );
        push({ tone: 'success', message: t('categoryAdmin.toast.updated') });
        await refresh(parentByChild[modal.node.id] ?? null);
      }
      setModal(null);
    } catch (err) {
      showApiError(err);
    } finally {
      setBusy(false);
    }
  };

  /**
   * Activate / deactivate. Works on TecDoc rows too — the flag is ours, not TecDoc's, and the
   * sync leaves it alone.
   *
   * Deactivating does TWO things, and the second is the one that needs confirming: the category
   * stops being offered in the pickers, AND every product filed under it (or under any
   * sub-category of it) drops out of every search on the platform — counter, customer catalogue,
   * cross-reference, sales. Nothing is written to those products, so switching the category back
   * on returns exactly what it took away.
   */
  const setActive = async (n: CatNode, active: boolean) => {
    if (!active) {
      const ok = await confirm({
        title: t('categoryAdmin.confirm.deactivateTitle', { name: n.name }),
        description: n.hasChildren
          ? t('categoryAdmin.confirm.deactivateDescriptionWithChildren')
          : t('categoryAdmin.confirm.deactivateDescription'),
        confirmLabel: t('categoryAdmin.confirm.deactivate'),
        tone: 'danger',
      });
      if (!ok) return;
    }
    try {
      await apiClient.patch(`/products/categories/${n.id}`, { active }, cmd());
      push({
        tone: 'success',
        message: active ? t('categoryAdmin.toast.activated') : t('categoryAdmin.toast.deactivated'),
      });
      await refresh(parentByChild[n.id] ?? null);
    } catch (err) {
      showApiError(err);
    }
  };

  /**
   * Switch one brand under one leaf. The server applies it to every tree copy of the same generic
   * article (TecDoc hangs one under several branches), so any copy already on screen is reloaded
   * to show the same state rather than contradicting the one just clicked.
   */
  const setBrandActive = async (n: CatNode, b: CatBrand, active: boolean) => {
    if (!active) {
      const ok = await confirm({
        title: t('categoryAdmin.confirm.deactivateBrandTitle', {
          brand: b.brandName,
          category: n.name,
        }),
        description:
          b.productCount > 0
            ? t('categoryAdmin.confirm.deactivateBrandDescriptionWithCount', {
                brand: b.brandName,
                category: n.name,
                count: b.productCount,
              })
            : t('categoryAdmin.confirm.deactivateBrandDescription', {
                brand: b.brandName,
                category: n.name,
              }),
        confirmLabel: t('categoryAdmin.confirm.deactivateBrand'),
        tone: 'danger',
      });
      if (!ok) return;
    }
    try {
      await apiClient.patch(
        `/products/categories/${n.id}/brands/${encodeURIComponent(b.brandKey)}`,
        { active },
        cmd()
      );
      push({
        tone: 'success',
        message: t(
          active ? 'categoryAdmin.toast.brandActivated' : 'categoryAdmin.toast.brandDeactivated',
          { brand: b.brandName, category: n.name }
        ),
      });
      const twins =
        n.kind === 'GENERIC' && n.tecdocId != null
          ? loadedNodes().filter((x) => x.kind === 'GENERIC' && x.tecdocId === n.tecdocId)
          : [n];
      await Promise.all(
        [...new Set(twins.map((x) => x.id))]
          .filter((id) => brandsMap[id] || id === n.id)
          .map(loadBrands)
      );
    } catch (err) {
      showApiError(err);
    }
  };

  /**
   * Load TecDoc's brand list under every leaf. It runs in the background on the server (the scan and
   * ~200k writes outlive a request), so this starts it and polls the sync log until it settles.
   */
  const syncBrands = async () => {
    setSyncingBrands(true);
    try {
      const { data } = await apiClient.post<{ data: { syncLogId: string } }>(
        '/admin/tecdoc/sync-category-brands',
        {},
        cmd()
      );
      const id = data.data.syncLogId;
      push({ tone: 'info', message: t('categoryAdmin.toast.brandSyncStarted') });
      for (let i = 0; i < 120; i++) {
        await new Promise((r) => setTimeout(r, 3000));
        const { data: log } = await apiClient.get<{ data: SyncLogRow[] }>(
          '/admin/tecdoc/sync-log?limit=20'
        );
        const row = log.data.find((r) => r.id === id);
        if (!row || row.status === 'RUNNING') continue;
        if (row.status === 'FAILED') {
          push({
            tone: 'error',
            title: t('categoryAdmin.toast.brandSyncFailedTitle'),
            message: row.error_log ?? t('categoryAdmin.toast.unknownError'),
          });
        } else {
          push({
            tone: 'success',
            title: t('categoryAdmin.toast.brandsSyncedTitle'),
            message: t('categoryAdmin.toast.brandsSyncedMessage', {
              count: row.category_brands_synced ?? 0,
            }),
          });
        }
        setChildrenMap({});
        setBrandsMap({});
        setExpanded(new Set());
        await refresh(null);
        return;
      }
      push({ tone: 'info', message: t('categoryAdmin.toast.brandSyncRunning') });
    } catch (err) {
      showApiError(err);
    } finally {
      setSyncingBrands(false);
    }
  };

  const renderBrands = (n: CatNode, depth: number, hiddenAbove: boolean): React.ReactNode => {
    const all = brandsMap[n.id];
    if (!all || all.length === 0) return null;
    const q = (brandFilter[n.id] ?? '').trim().toLowerCase();
    const matched = q ? all.filter((b) => b.brandName.toLowerCase().includes(q)) : all;
    const showAll = brandShowAll.has(n.id) || q.length > 0;
    const shown = showAll ? matched : matched.slice(0, BRAND_PAGE);
    const offCount = all.filter((b) => !b.active).length;
    const pad = (depth + 1) * 18 + 4;
    return (
      <div
        className="border-l border-dashed border-muted-foreground/30"
        style={{ marginLeft: pad - 6 }}
      >
        <div className="flex flex-wrap items-center gap-2 py-1 pl-2 text-xs text-muted-foreground">
          <span className="font-medium uppercase tracking-wide">
            {t('categoryAdmin.brands.heading')}
          </span>
          <span>
            {all.length}
            {offCount > 0 && (
              <span className="text-destructive">
                {' '}
                · {t('categoryAdmin.brands.offCount', { count: offCount })}
              </span>
            )}
          </span>
          {all.length > 12 && (
            <input
              value={brandFilter[n.id] ?? ''}
              onChange={(e) => setBrandFilter((m) => ({ ...m, [n.id]: e.target.value }))}
              placeholder={t('categoryAdmin.brands.filterPlaceholder')}
              className={`${inputClass} h-7 w-44 py-0 text-xs`}
            />
          )}
        </div>
        {shown.map((b) => (
          <div
            key={b.brandKey}
            className={`flex items-center gap-1.5 rounded py-0.5 pl-2 pr-1 text-sm hover:bg-muted/40 ${
              !b.active || hiddenAbove ? 'opacity-60' : ''
            }`}
          >
            <span className={b.active ? '' : 'text-muted-foreground line-through'}>
              {b.brandName}
            </span>
            {!b.active ? (
              <span
                className="text-[10px] px-1 rounded bg-destructive/10 text-destructive"
                title={t('categoryAdmin.brands.inactiveHiddenTitle', {
                  brand: b.brandName,
                  category: n.name,
                })}
              >
                {t('categoryAdmin.node.inactiveHidden')}
              </span>
            ) : hiddenAbove ? (
              <span
                className="text-[10px] px-1 rounded bg-muted text-muted-foreground"
                title={t('categoryAdmin.brands.hiddenByCategoryTitle')}
              >
                {t('categoryAdmin.brands.hiddenByCategory')}
              </span>
            ) : null}
            <span
              className={`text-[10px] px-1 rounded ${b.source === 'TECDOC' ? 'bg-muted text-muted-foreground' : 'bg-success/15 text-success'}`}
              title={
                b.source === 'TECDOC'
                  ? t('categoryAdmin.brands.tecdocSourceTitle')
                  : t('categoryAdmin.brands.catalogSourceTitle')
              }
            >
              {b.source === 'TECDOC' ? 'TecDoc' : t('categoryAdmin.brands.ourCatalog')}
            </span>
            {b.productCount > 0 && (
              <span className="text-[10px] text-muted-foreground">
                {t('categoryAdmin.node.productCount', { count: b.productCount })}
              </span>
            )}
            {b.articleCount > 0 && (
              <span className="text-[10px] text-muted-foreground/70">
                {t('categoryAdmin.brands.tecdocArticles', { count: b.articleCount })}
              </span>
            )}
            {canWrite && (
              <span className="ml-auto opacity-70">
                <button
                  title={
                    b.active
                      ? t('categoryAdmin.brands.deactivateTitle', { brand: b.brandName })
                      : t('categoryAdmin.brands.activateTitle', { brand: b.brandName })
                  }
                  onClick={() => setBrandActive(n, b, !b.active)}
                  className={`p-1 ${b.active ? 'hover:text-destructive' : 'text-success hover:text-success'}`}
                >
                  {b.active ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                </button>
              </span>
            )}
          </div>
        ))}
        {!showAll && matched.length > BRAND_PAGE && (
          <button
            className="py-1 pl-2 text-xs text-primary hover:underline"
            onClick={() => setBrandShowAll((s) => new Set(s).add(n.id))}
          >
            {t('categoryAdmin.brands.showAll', { count: matched.length })}
          </button>
        )}
        {q && matched.length === 0 && (
          <div className="py-1 pl-2 text-xs text-muted-foreground">
            {t('categoryAdmin.brands.noMatch')}
          </div>
        )}
      </div>
    );
  };

  const del = async (n: CatNode) => {
    const ok = await confirm({
      title: t('categoryAdmin.confirm.deleteTitle', { name: n.name }),
      description: t('categoryAdmin.confirm.deleteDescription'),
      confirmLabel: t('categoryAdmin.confirm.delete'),
      tone: 'danger',
    });
    if (!ok) return;
    try {
      await apiClient.delete(`/products/categories/${n.id}`, cmd());
      push({ tone: 'success', message: t('categoryAdmin.toast.deleted') });
      await refresh(parentByChild[n.id] ?? null);
    } catch (err) {
      showApiError(err);
    }
  };

  // `ancestorInactive`: a live child of a switched-off parent is still hidden from the pickers,
  // so it is shown dimmed with a note rather than looking offered.
  const renderNodes = (
    nodes: CatNode[],
    depth: number,
    ancestorInactive = false
  ): React.ReactNode =>
    nodes.map((n) => (
      <Fragment key={n.id}>
        <div
          className={`flex items-center gap-1.5 rounded px-1 py-1 text-sm hover:bg-muted/40 ${
            !n.active || ancestorInactive ? 'opacity-60' : ''
          }`}
          style={{ paddingLeft: depth * 18 + 4 }}
        >
          {n.hasChildren || n.hasBrands ? (
            <button
              onClick={() => toggle(n)}
              className="text-muted-foreground"
              aria-label={t('categoryAdmin.expand')}
            >
              {loadingNode.has(n.id) ? (
                <RefreshCw className="h-3.5 w-3.5 animate-spin" />
              ) : expanded.has(n.id) ? (
                <ChevronDown className="h-3.5 w-3.5" />
              ) : (
                <ChevronRight className="h-3.5 w-3.5" />
              )}
            </button>
          ) : (
            <span className="inline-block w-3.5" />
          )}
          {/* THE TECDOC ID, at every level of the tree — node and generic article alike.
              The catalogue tooling the staff came from prints it beside every category and
              subcategory, and it is the only thing that lines the two trees up: names are
              translated and re-worded between releases, our `code` is ours alone. Click to copy,
              because the reason anyone reads it is to paste it into the other system. */}
          {n.tecdocId != null && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                void navigator.clipboard?.writeText(String(n.tecdocId));
                push({
                  tone: 'success',
                  message: t('categoryAdmin.toast.tecdocIdCopied', { id: n.tecdocId }),
                });
              }}
              title={t('categoryAdmin.node.copyTitle', {
                kind: n.kind === 'GENERIC' ? 'PT_ID' : 'STR_ID',
                id: n.tecdocId,
              })}
              className="rounded bg-muted px-1 font-mono text-[10px] tabular-nums text-muted-foreground hover:bg-accent hover:text-foreground"
            >
              {n.tecdocId}
            </button>
          )}
          <span className={n.active ? '' : 'text-muted-foreground line-through'}>{n.name}</span>
          {!n.active ? (
            <span
              className="text-[10px] px-1 rounded bg-destructive/10 text-destructive"
              title={t('categoryAdmin.node.inactiveTitle')}
            >
              {t('categoryAdmin.node.inactiveHidden')}
            </span>
          ) : ancestorInactive ? (
            <span
              className="text-[10px] px-1 rounded bg-muted text-muted-foreground"
              title={t('categoryAdmin.node.hiddenByParentTitle')}
            >
              {t('categoryAdmin.node.hiddenByParent')}
            </span>
          ) : null}
          {n.kind === 'GENERIC' && (
            <span className="text-[10px] px-1 rounded bg-primary/10 text-primary">
              {t('categoryAdmin.node.genericArticle')}
            </span>
          )}
          <span
            className={`text-[10px] px-1 rounded ${n.source === 'TECDOC' ? 'bg-muted text-muted-foreground' : 'bg-success/15 text-success'}`}
          >
            {n.source === 'TECDOC' ? 'TecDoc' : t('categoryAdmin.node.custom')}
          </span>
          {n.productCount > 0 && (
            <span className="text-[10px] text-muted-foreground">
              {t('categoryAdmin.node.productCount', { count: n.productCount })}
            </span>
          )}
          {canWrite && (
            <span className="ml-auto flex items-center gap-0.5 opacity-70">
              <button
                title={
                  n.active
                    ? t('categoryAdmin.node.deactivateTitle')
                    : t('categoryAdmin.node.activateTitle')
                }
                onClick={() => setActive(n, !n.active)}
                className={`p-1 ${n.active ? 'hover:text-destructive' : 'text-success hover:text-success'}`}
              >
                {n.active ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
              </button>
              <button
                title={t('categoryAdmin.node.addSubcategory')}
                onClick={() =>
                  setModal({ mode: 'create', parentId: n.id, parentName: n.name, name: '' })
                }
                className="p-1 hover:text-foreground"
              >
                <Plus className="h-3.5 w-3.5" />
              </button>
              {n.source === 'CUSTOM' && (
                <>
                  <button
                    title={t('categoryAdmin.node.rename')}
                    onClick={() =>
                      setModal({ mode: 'edit', parentId: null, node: n, name: n.name })
                    }
                    className="p-1 hover:text-foreground"
                  >
                    <Pencil className="h-3.5 w-3.5" />
                  </button>
                  <button
                    title={t('categoryAdmin.node.delete')}
                    onClick={() => del(n)}
                    className="p-1 hover:text-destructive"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </>
              )}
            </span>
          )}
        </div>
        {expanded.has(n.id) &&
          childrenMap[n.id] &&
          (childrenMap[n.id].length > 0 ? (
            renderNodes(childrenMap[n.id], depth + 1, ancestorInactive || !n.active)
          ) : !n.hasBrands ? (
            <div
              className="text-xs text-muted-foreground py-1"
              style={{ paddingLeft: (depth + 1) * 18 + 18 }}
            >
              {t('categoryAdmin.emptyNode')}
            </div>
          ) : null)}
        {expanded.has(n.id) && renderBrands(n, depth, ancestorInactive || !n.active)}
      </Fragment>
    ));

  return (
    <div>
      {confirmDialog}
      <PageHeader
        title={t('categoryAdmin.title')}
        description={t('categoryAdmin.description')}
        actions={
          canWrite && (
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={() => setModal({ mode: 'create', parentId: null, name: '' })}
              >
                <Plus className="h-4 w-4" /> {t('categoryAdmin.rootCategory')}
              </Button>
              <Button size="sm" variant="outline" onClick={syncBrands} disabled={syncingBrands}>
                <RefreshCw className={`h-4 w-4 ${syncingBrands ? 'animate-spin' : ''}`} />
                {syncingBrands ? t('categoryAdmin.syncingBrands') : t('categoryAdmin.syncBrands')}
              </Button>
              <Button size="sm" onClick={sync} disabled={syncing}>
                <RefreshCw className={`h-4 w-4 ${syncing ? 'animate-spin' : ''}`} />
                {syncing ? t('categoryAdmin.syncing') : t('categoryAdmin.syncTecdoc')}
              </Button>
            </div>
          )
        }
      />

      <div className="rounded-lg border bg-card p-3 shadow-sm">
        {roots === null ? (
          <p className="text-sm text-muted-foreground">{t('categoryAdmin.loading')}</p>
        ) : roots.length === 0 ? (
          <div className="py-8 text-center text-sm text-muted-foreground">
            <FolderTree className="mx-auto mb-2 h-8 w-8 opacity-50" />
            {t('categoryAdmin.empty')}
          </div>
        ) : (
          renderNodes(roots, 0)
        )}
      </div>

      {modal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-md space-y-3 rounded-lg border bg-card p-5 shadow-lg">
            <h2 className="text-lg font-semibold">
              {modal.mode === 'create'
                ? t('categoryAdmin.modal.newCategory')
                : t('categoryAdmin.modal.renameCategory')}
            </h2>
            {modal.mode === 'create' && (
              <p className="text-xs text-muted-foreground">
                {t('categoryAdmin.modal.parent', {
                  name: modal.parentName ?? t('categoryAdmin.modal.rootParent'),
                })}
              </p>
            )}
            <input
              autoFocus
              value={modal.name}
              onChange={(e) => setModal({ ...modal, name: e.target.value })}
              onKeyDown={(e) => e.key === 'Enter' && submitModal()}
              placeholder={t('categoryAdmin.modal.namePlaceholder')}
              className={inputClass}
            />
            <div className="flex justify-end gap-2">
              <Button size="sm" variant="outline" disabled={busy} onClick={() => setModal(null)}>
                <X className="h-4 w-4" />
                {t('categoryAdmin.modal.cancel')}
              </Button>
              <Button size="sm" disabled={busy || !modal.name.trim()} onClick={submitModal}>
                <Save className="h-4 w-4" />
                {busy ? t('categoryAdmin.modal.saving') : t('categoryAdmin.modal.save')}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
